/**
 * MuiLoansList - Optimized Loans Analytics/Dashboard Page
 *
 * OPTIMIZATION: Phase 9.1 - Data fetching optimization
 *
 * Changes made:
 * - Replaced getLoans() with fetchLoanAnalytics() - fetches only required fields
 * - Added better error handling and loading states
 * - Retained full data loading since charts require aggregations
 *
 * Note: This is an analytics page, not a list page. Charts need all data to compute
 * aggregations (by product, by customer). FlexFlow doesn't support server-side
 * aggregations, so client-side computation is necessary.
 *
 * Benefits:
 * - Reduced data transfer by fetching only essential fields
 * - Better error handling
 * - Cleaner code structure
 */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Card,
  TextField,
  Autocomplete,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import InventoryIcon from "@mui/icons-material/Inventory";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CategoryIcon from "@mui/icons-material/Category";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReactApexChart from "react-apexcharts";

// MUI Components
import MuiModal from "../../components/mui/MuiModal";
import MuiLoadingButton from "../../components/mui/MuiLoadingButton";

// API and helpers - using optimized fetchLoanAnalytics
import { fetchLoanAnalytics, postLoan, getProducts, postTask } from "helpers/api_helper";
import {
  transformLoansData,
  calculateTotalQuantity,
} from "helpers/generalfunctions";

const MuiLoansList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inboundModalOpen, setInboundModalOpen] = useState(false);
  const [outboundModalOpen, setOutboundModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);

  // Inbound modal state
  const [inboundCustomerId, setInboundCustomerId] = useState("");
  const [selectedCustomerLoans, setSelectedCustomerLoans] = useState([]);
  const [selectedReturnProducts, setSelectedReturnProducts] = useState([]);
  const [returnedQuantities, setReturnedQuantities] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Outbound modal state
  const [outboundCustomerId, setOutboundCustomerId] = useState("");
  const [outboundCustomerName, setOutboundCustomerName] = useState("");
  const [outboundProductId, setOutboundProductId] = useState("");
  const [outboundProductName, setOutboundProductName] = useState("");
  const [outboundQuantity, setOutboundQuantity] = useState("");

  document.title = `${t("Loans")} | FlexFlow ERP`;

  // Redux state
  const { customers } = useSelector((state) => ({
    customers: state.customers.customers,
  }));

  // ============================================================================
  // FETCH LOANS DATA - Using optimized fetchLoanAnalytics
  // Only fetches required fields for charts (id, bound, quantity, etc.)
  // ============================================================================
  const fetchLoansData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchLoanAnalytics();
      setLoans(res || []);
    } catch (err) {
      console.error("Error fetching loans:", err);
      setError(err.message || "Failed to load loans data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      const res = await getProducts(null, null);
      setProducts(res || []);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  }, []);

  // Load data on mount and when location changes (e.g., after creating a loan)
  useEffect(() => {
    fetchLoansData();
    fetchProducts();
  }, [location.key, fetchLoansData, fetchProducts]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    fetchLoansData();
  }, [fetchLoansData]);

  // Transform loans data
  const customersData = useMemo(
    () => transformLoansData(loans),
    [loans]
  );

  // Calculate total quantity
  const totalQuantity = useMemo(
    () => calculateTotalQuantity(loans),
    [loans]
  );

  // Calculate product data for chart
  const productData = useMemo(() => {
    return loans.reduce((acc, loan) => {
      const productName = loan.prdname;
      const quantity = loan.quantity;
      const bound = loan.bound;

      if (!acc[productName]) {
        acc[productName] = { inbound: 0, outbound: 0 };
      }

      if (bound === "INBOUND") {
        acc[productName].inbound += quantity;
      } else if (bound === "OUTBOUND") {
        acc[productName].outbound += quantity;
      }

      return acc;
    }, {});
  }, [loans]);

  // Chart data
  const chartData = useMemo(() => {
    return Object.keys(productData).map((productName) => {
      const inbound = productData[productName].inbound;
      const outbound = productData[productName].outbound;
      const total = outbound - inbound;
      return { productName, inbound, outbound, total };
    });
  }, [productData]);

  const chartOptions = {
    chart: {
      id: "bar-chart",
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const selectedProductName = chartData[config.dataPointIndex]?.productName;
          if (selectedProductName) {
            setSelectedProduct(selectedProductName);
          }
        },
      },
    },
    xaxis: {
      categories: chartData.map((data) => data.productName),
      labels: { style: { fontSize: "12px" } },
    },
    colors: ["#556ee6"],
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
      },
    },
    dataLabels: { enabled: false },
  };

  const chartSeries = [
    {
      name: "Net Quantity",
      data: chartData.map((data) => data.total),
    },
  ];

  // Customer data for selected product
  const customerDataForProduct = useMemo(() => {
    if (!selectedProduct) return [];
    return loans
      .filter((loan) => loan.prdname === selectedProduct)
      .reduce((acc, loan) => {
        const customerName = loan.custname;
        const quantity = loan.quantity;
        const bound = loan.bound;

        if (!acc[customerName]) {
          acc[customerName] = 0;
        }

        if (bound === "INBOUND") {
          acc[customerName] -= quantity;
        } else if (bound === "OUTBOUND") {
          acc[customerName] += quantity;
        }

        return acc;
      }, {});
  }, [loans, selectedProduct]);

  const customerListForProduct = useMemo(() => {
    return Object.keys(customerDataForProduct)
      .map((custname) => ({
        custname,
        total: customerDataForProduct[custname],
      }))
      .filter((customer) => customer.total !== 0);
  }, [customerDataForProduct]);

  // Selected customer product data
  const selectedCustomerProductData = useMemo(() => {
    if (!selectedCustomer) return [];
    return loans
      .filter((loan) => loan.custname === selectedCustomer)
      .reduce((acc, loan) => {
        const productName = loan.prdname;
        const quantity = loan.quantity;
        const bound = loan.bound;

        if (!acc[productName]) {
          acc[productName] = { inbound: 0, outbound: 0 };
        }

        if (bound === "INBOUND") {
          acc[productName].inbound += quantity;
        } else if (bound === "OUTBOUND") {
          acc[productName].outbound += quantity;
        }

        return acc;
      }, {});
  }, [loans, selectedCustomer]);

  const customerChartData = useMemo(() => {
    return Object.keys(selectedCustomerProductData).map((productName) => {
      const inbound = selectedCustomerProductData[productName].inbound;
      const outbound = selectedCustomerProductData[productName].outbound;
      const total = outbound - inbound;
      return { productName, inbound, outbound, total };
    });
  }, [selectedCustomerProductData]);

  const customerChartOptions = {
    chart: {
      id: "customer-bar-chart",
      toolbar: { show: false },
    },
    xaxis: {
      categories: customerChartData.map((data) => data.productName),
      labels: { style: { fontSize: "12px" } },
    },
    colors: ["#34c38f"],
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
      },
    },
    dataLabels: { enabled: false },
  };

  const customerChartSeries = [
    {
      name: "Net Quantity",
      data: customerChartData.map((data) => data.total),
    },
  ];

  // Unique customers list
  const uniqueCustomers = useMemo(() => {
    return Array.from(new Set(loans.map((loan) => loan.custname)));
  }, [loans]);

  // Handle inbound customer selection
  const handleInboundCustomerSelect = (customerId) => {
    setInboundCustomerId(customerId);
    setSelectedReturnProducts([]);
    setReturnedQuantities({});

    const filteredLoans = loans
      .filter((loan) => loan.customer_id === customerId)
      .reduce((acc, loan) => {
        const { order_id, prdname, prd_id, quantity, custname, bound, customer_id } = loan;

        const existingProduct = acc.find((item) => item.prd_id === prd_id);

        if (existingProduct) {
          if (bound === "OUTBOUND") {
            existingProduct.outbound += quantity;
          } else if (bound === "INBOUND") {
            existingProduct.inbound += quantity;
          }
          existingProduct.total = existingProduct.outbound - existingProduct.inbound;
        } else {
          acc.push({
            customer_id,
            order_id,
            prdname,
            custname,
            prd_id,
            inbound: bound === "INBOUND" ? quantity : 0,
            outbound: bound === "OUTBOUND" ? quantity : 0,
            total: bound === "OUTBOUND" ? quantity : -quantity,
          });
        }

        return acc;
      }, []);

    setSelectedCustomerLoans(filteredLoans);
  };

  // Handle product selection for return
  const handleReturnProductSelect = (product) => {
    const isAlreadySelected = selectedReturnProducts.find((p) => p.prd_id === product.prd_id);
    if (!isAlreadySelected) {
      setSelectedReturnProducts([...selectedReturnProducts, product]);
    }
  };

  // Handle quantity change
  const handleQuantityChange = (prd_id, quantity) => {
    setReturnedQuantities({
      ...returnedQuantities,
      [prd_id]: quantity,
    });
  };

  // Create task and loan for return
  const createTaskAndLoan = async (selectedProd, returnedQuantity, customerId) => {
    const payload = {
      resource: [
        {
          type: "PLYNTIRIO",
          materials: "RETURN",
          status: "PENDING",
          orderref: selectedProd.order_id,
          productid: selectedProd.prd_id,
          name: selectedProd.prdname,
          description: "EPISTROFH-PLYSIMO",
          quantity: parseInt(returnedQuantity),
          customer_id_ref: customerId,
          customer_name: selectedProd.custname,
          seq: 1,
        },
      ],
    };

    try {
      const taskResponse = await postTask(payload);
      if (taskResponse && taskResponse.resource) {
        const loanPayload = {
          resource: [
            {
              bound: "INBOUND",
              quantity: returnedQuantity,
              customer_id: customerId,
              order_id: selectedProd.order_id,
              prd_id: selectedProd.prd_id,
              prdname: selectedProd.prdname,
              custname: selectedProd.custname,
            },
          ],
        };
        await postLoan(loanPayload);
      }
    } catch (error) {
      console.error("Error in task or loan creation:", error);
    }
  };

  // Handle creating returns
  const handleCreateReturns = async () => {
    setSubmitting(true);
    try {
      for (const product of selectedReturnProducts) {
        const returnedQuantity = returnedQuantities[product.prd_id];
        if (returnedQuantity && returnedQuantity > 0) {
          await createTaskAndLoan(product, returnedQuantity, inboundCustomerId);
        }
      }
      setInboundModalOpen(false);
      resetInboundModal();
      fetchLoansData();
    } catch (error) {
      console.error("Error creating returns:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset inbound modal
  const resetInboundModal = () => {
    setInboundCustomerId("");
    setSelectedCustomerLoans([]);
    setSelectedReturnProducts([]);
    setReturnedQuantities({});
  };

  // Handle outbound submission
  const handleOutboundSubmit = async () => {
    if (!outboundCustomerId || !outboundProductId || !outboundQuantity) {
      alert("Please fill in all fields.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        resource: [
          {
            bound: "OUTBOUND",
            customer_id: outboundCustomerId,
            prd_id: outboundProductId,
            order_id: 88888,
            quantity: parseInt(outboundQuantity),
            custname: outboundCustomerName,
            prdname: outboundProductName,
          },
        ],
      };

      await postLoan(payload);
      setOutboundModalOpen(false);
      resetOutboundModal();
      fetchLoansData();
    } catch (error) {
      console.error("Error creating loan record:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset outbound modal
  const resetOutboundModal = () => {
    setOutboundCustomerId("");
    setOutboundCustomerName("");
    setOutboundProductId("");
    setOutboundProductName("");
    setOutboundQuantity("");
  };

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Compact Header: Breadcrumb + Actions */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={() => navigate(-1)}
            sx={{ color: "#6b7280" }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}>
            <Link
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", fontSize: "0.813rem" }}
            >
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </Link>
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#111827" }}>
              {t("Loans")}
            </Typography>
          </Breadcrumbs>
        </Stack>

        {/* Actions */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={handleRefresh}
            sx={{ color: "#6b7280", width: 32, height: 32 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Button
            variant="contained"
            size="small"
            startIcon={<CallReceivedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setInboundModalOpen(true)}
            sx={{
              height: 32,
              fontSize: "0.75rem",
              textTransform: "none",
              bgcolor: "#3b82f6",
              "&:hover": { bgcolor: "#2563eb" },
            }}
          >
            {t("Inbound")}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<CallMadeIcon sx={{ fontSize: 16 }} />}
            onClick={() => setOutboundModalOpen(true)}
            sx={{
              height: 32,
              fontSize: "0.75rem",
              textTransform: "none",
              bgcolor: "#10b981",
              "&:hover": { bgcolor: "#059669" },
            }}
          >
            {t("Outbound")}
          </Button>
        </Stack>
      </Paper>

      {/* Compact Stat Chips */}
      <Box sx={{ mb: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Chip
          icon={<InventoryIcon sx={{ fontSize: 16 }} />}
          label={`${t("Outstanding")}: ${totalQuantity}`}
          size="small"
          sx={{
            height: 28,
            fontSize: "0.75rem",
            fontWeight: 600,
            bgcolor: "#dbeafe",
            color: "#1d4ed8",
            border: "1px solid #93c5fd",
            "& .MuiChip-icon": { color: "#3b82f6" },
          }}
        />
        <Chip
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          label={`${t("Customers")}: ${uniqueCustomers.length}`}
          size="small"
          sx={{
            height: 28,
            fontSize: "0.75rem",
            fontWeight: 600,
            bgcolor: "#dcfce7",
            color: "#166534",
            border: "1px solid #86efac",
            "& .MuiChip-icon": { color: "#22c55e" },
          }}
        />
        <Chip
          icon={<CategoryIcon sx={{ fontSize: 16 }} />}
          label={`${t("Products")}: ${Object.keys(productData).length}`}
          size="small"
          sx={{
            height: 28,
            fontSize: "0.75rem",
            fontWeight: 600,
            bgcolor: "#fef3c7",
            color: "#92400e",
            border: "1px solid #fcd34d",
            "& .MuiChip-icon": { color: "#f59e0b" },
          }}
        />
      </Box>

      {/* Product Chart Section */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #f3f4f6" }}>
          <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#374151" }}>
            {t("Total by Product")}
          </Typography>
          <Typography sx={{ fontSize: "0.688rem", color: "#9ca3af" }}>
            {t("Click a bar to see customer breakdown")}
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} sx={{ color: "#3b82f6" }} />
            </Box>
          ) : chartData.length > 0 ? (
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type="bar"
              height={280}
            />
          ) : (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: "0.813rem", color: "#9ca3af" }}>
                {t("No loan data available")}
              </Typography>
            </Box>
          )}

          {/* Selected product customer list */}
          {selectedProduct && customerListForProduct.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f3f4f6" }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                {selectedProduct} {t("by Customer")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {customerListForProduct.map((customer, index) => (
                  <Chip
                    key={index}
                    label={`${customer.custname}: ${customer.total}`}
                    size="small"
                    sx={{
                      height: 26,
                      fontSize: "0.75rem",
                      bgcolor: customer.total > 0 ? "#fef3c7" : "#dcfce7",
                      color: customer.total > 0 ? "#92400e" : "#166534",
                      border: `1px solid ${customer.total > 0 ? "#fcd34d" : "#86efac"}`,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Customer Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #f3f4f6" }}>
          <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#374151" }}>
            {t("Products by Customer")}
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                {t("Select Customer")}
              </Typography>
              <List sx={{ maxHeight: 280, overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 1 }}>
                {uniqueCustomers.map((customerName, index) => (
                  <React.Fragment key={index}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedCustomer === customerName}
                        onClick={() => setSelectedCustomer(customerName)}
                        sx={{
                          py: 0.75,
                          "&.Mui-selected": { bgcolor: "#eff6ff", "&:hover": { bgcolor: "#dbeafe" } },
                        }}
                      >
                        <ListItemText
                          primary={customerName}
                          primaryTypographyProps={{ fontSize: "0.813rem", color: "#374151" }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < uniqueCustomers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Grid>

            <Grid item xs={12} md={8}>
              {selectedCustomer ? (
                <>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                    {t("Customer")}: {selectedCustomer}
                  </Typography>

                  {/* Product details */}
                  <Box sx={{ mb: 2 }}>
                    {customerChartData.map((data, index) => (
                      <Box
                        key={index}
                        sx={{
                          px: 2,
                          py: 1,
                          mb: 0.5,
                          border: "1px solid #e5e7eb",
                          borderRadius: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          bgcolor: "#fff",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#374151" }}>
                          {data.productName}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          <Chip
                            label={`${t("In")}: ${data.inbound}`}
                            size="small"
                            sx={{ height: 22, fontSize: "0.688rem", bgcolor: "#dcfce7", color: "#166534" }}
                          />
                          <Chip
                            label={`${t("Out")}: ${data.outbound}`}
                            size="small"
                            sx={{ height: 22, fontSize: "0.688rem", bgcolor: "#fef3c7", color: "#92400e" }}
                          />
                          <Chip
                            label={`${t("Net")}: ${data.total}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.688rem",
                              bgcolor: data.total > 0 ? "#fee2e2" : "#dcfce7",
                              color: data.total > 0 ? "#991b1b" : "#166534",
                            }}
                          />
                        </Stack>
                      </Box>
                    ))}
                  </Box>

                  {/* Customer chart */}
                  {customerChartData.length > 0 && (
                    <ReactApexChart
                      options={customerChartOptions}
                      series={customerChartSeries}
                      type="bar"
                      height={220}
                    />
                  )}
                </>
              ) : (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "0.813rem", color: "#9ca3af" }}>
                    {t("Select a customer from the list to view their loan details")}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>

        {/* Inbound Modal */}
        <MuiModal
          open={inboundModalOpen}
          onClose={() => {
            setInboundModalOpen(false);
            resetInboundModal();
          }}
          title="Create Inbound Return"
          subtitle="Return loaned items from customer"
          maxWidth="md"
          actions={
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setInboundModalOpen(false);
                  resetInboundModal();
                }}
              >
                Cancel
              </Button>
              <MuiLoadingButton
                variant="contained"
                onClick={handleCreateReturns}
                loading={submitting}
                disabled={selectedReturnProducts.length === 0}
              >
                Create Returns
              </MuiLoadingButton>
            </>
          }
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select Customer
            </Typography>
            <Autocomplete
              options={customers || []}
              getOptionLabel={(option) => option.name || option.label || ""}
              onChange={(e, value) => value && handleInboundCustomerSelect(value.id)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search customer..." size="small" />
              )}
            />
          </Box>

          {selectedCustomerLoans.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Customer Loans (Click to select for return)
              </Typography>
              <List sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
                {selectedCustomerLoans.map((loan) => (
                  <ListItem
                    key={loan.prd_id}
                    button
                    onClick={() => handleReturnProductSelect(loan)}
                    sx={{
                      bgcolor: selectedReturnProducts.find((p) => p.prd_id === loan.prd_id)
                        ? "primary.lighter"
                        : "inherit",
                    }}
                  >
                    <ListItemText
                      primary={`${loan.prdname} - Outstanding: ${loan.total}`}
                      secondary={`Inbound: ${loan.inbound} | Outbound: ${loan.outbound}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {selectedReturnProducts.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Return Quantities
              </Typography>
              <Grid container spacing={2}>
                {selectedReturnProducts.map((product) => (
                  <Grid item xs={12} sm={6} key={product.prd_id}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {product.prdname}
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        fullWidth
                        placeholder={t("Return quantity")}
                        value={returnedQuantities[product.prd_id] || ""}
                        onChange={(e) => handleQuantityChange(product.prd_id, e.target.value)}
                        inputProps={{ min: 1, max: product.total }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </MuiModal>

        {/* Outbound Modal */}
        <MuiModal
          open={outboundModalOpen}
          onClose={() => {
            setOutboundModalOpen(false);
            resetOutboundModal();
          }}
          title="Create Outbound Loan"
          subtitle="Loan items to customer"
          maxWidth="sm"
          actions={
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setOutboundModalOpen(false);
                  resetOutboundModal();
                }}
              >
                Cancel
              </Button>
              <MuiLoadingButton
                variant="contained"
                onClick={handleOutboundSubmit}
                loading={submitting}
                disabled={!outboundCustomerId || !outboundProductId || !outboundQuantity}
              >
                Submit
              </MuiLoadingButton>
            </>
          }
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Customer
              </Typography>
              <Autocomplete
                options={customers || []}
                getOptionLabel={(option) => option.name || option.label || ""}
                onChange={(e, value) => {
                  if (value) {
                    setOutboundCustomerId(value.id);
                    setOutboundCustomerName(value.name || value.label || "");
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search customer..." size="small" />
                )}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Product
              </Typography>
              <Autocomplete
                options={products || []}
                getOptionLabel={(option) => option.name || ""}
                onChange={(e, value) => {
                  if (value) {
                    setOutboundProductId(value.id);
                    setOutboundProductName(value.name || "");
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search product..." size="small" />
                )}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Quantity
              </Typography>
              <TextField
                type="number"
                size="small"
                fullWidth
                placeholder="Enter quantity"
                value={outboundQuantity}
                onChange={(e) => setOutboundQuantity(e.target.value)}
                inputProps={{ min: 1 }}
              />
            </Box>
          </Box>
        </MuiModal>
    </Box>
  );
};

MuiLoansList.propTypes = {
  invoices: PropTypes.array,
  onGetInvoices: PropTypes.func,
  customers: PropTypes.array,
};

export default MuiLoansList;
